import {
    FormattedEvent,
    transformDestinationEvent,
} from '@eventbrite/event-renderer';
import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import {
    EventBasicInformation,
    EventBasicInformationContext,
} from '../../../contexts';
import { getSortedEvents } from '../../_shared/getSortedEvents';
import {
    getRelatedEvents,
    getRelatedEventsByCategory,
    getRelatedEventsByLocation,
} from '../api';
import { RelatedEventsProps } from '../types';

export function getOtherEventsYouMayLikeQueryKey(eventId: string) {
    return ['related-events', 'other-organizers', eventId];
}

export const EVENT_DUPLICATES_QUERY_KEY = [
    'related-events',
    'events-to-filter',
];

const emptyRelatedEvents = {
    events: [] as FormattedEvent[],
    promotedEvents: [] as FormattedEvent[],
};

export type UseOtherEventsYouMayLikeProps = Pick<
    RelatedEventsProps,
    'isAuthenticated' | 'eventId'
> & { suspense?: boolean; eventsToRemove?: Pick<FormattedEvent, 'id'>[] };

export function useOtherEventsYouMayLike({
    isAuthenticated,
    eventId,
    eventsToRemove = [],
    suspense = false,
}: UseOtherEventsYouMayLikeProps) {
    const eventBasicInformation = useContext(EventBasicInformationContext);

    const { data = emptyRelatedEvents } = useQuery({
        queryKey: getOtherEventsYouMayLikeQueryKey(eventId),
        queryFn: async () =>
            getRelatedEvents(
                isAuthenticated,
                eventBasicInformation as EventBasicInformation,
            ),
        suspense,
        staleTime: Infinity,
        enabled: !!eventBasicInformation,
    });

    const organicEvents = useEventsWithoutDuplicates(
        data.events,
        eventsToRemove,
    );

    const sortedEvents = useMemo(
        () => getSortedEvents(organicEvents, data.promotedEvents || []),
        [organicEvents, data.promotedEvents],
    );

    return sortedEvents;
}

function useEventsWithoutDuplicates(
    events: FormattedEvent[],
    eventsToRemove: Pick<FormattedEvent, 'id'>[],
) {
    const idsToFilter = useMemo(
        () => new Set(eventsToRemove.map(({ id }) => id)),
        [eventsToRemove],
    );

    return useMemo(
        () => events.filter(({ id }) => !idsToFilter.has(id)),
        [events, idsToFilter],
    );
}

// fetches popular events from a category
export type UseOtherEventsYouMayLikeByCategoryProps = {
    category: string | null;
    placeId: string | null;
    disabled: boolean;
    suspense: boolean;
};

export function useOtherEventsYouMayLikeByCategory({
    category,
    placeId,
    disabled = false,
    suspense = false,
}: UseOtherEventsYouMayLikeByCategoryProps): FormattedEvent[] {
    const { data = emptyRelatedEvents } = useQuery({
        queryKey: ['category-events', placeId],
        queryFn: () => getRelatedEventsByCategory(category, placeId),
        suspense,
        staleTime: Infinity,
        enabled: !disabled && !!category && !!placeId,
    });
    return [...data.events, ...data.promotedEvents].map(
        transformDestinationEvent,
    );
}

// fetches popular events from a location
export type UseOtherEventsYouMayLikeByLocationProps = {
    placeId: string | null;
    disabled: boolean;
    suspense: boolean;
};

export function useOtherEventsYouMayLikeByLocation({
    placeId,
    disabled = false,
    suspense = false,
}: UseOtherEventsYouMayLikeByLocationProps): FormattedEvent[] {
    const { data = emptyRelatedEvents } = useQuery({
        queryKey: ['location-events', placeId],
        queryFn: () => getRelatedEventsByLocation(placeId),
        suspense,
        staleTime: Infinity,
        enabled: !disabled && !!placeId,
    });
    return [...data.events, ...data.promotedEvents].map(
        transformDestinationEvent,
    );
}
